import { buffers, eventChannel } from "redux-saga";

// The school database is not readable by the apps except for <branchPath>/lastUpdate (see
// database.rules.school.json in the backend repo). Each entry there is a timestamp the
// backend bumps whenever that data changes; a screen listens to it and re-fetches the data
// through the API.

// Bursts of writes (a bulk attendance save, a booking touching several entities) land as
// several bumps within moments of each other; one re-fetch answers all of them.
const SIGNAL_DEBOUNCE_MS = 300;

/**
 * Emits once as soon as it is opened, then again after every change to the entry.
 *
 * The value is never empty: an entry nobody has bumped yet, a session without access to the
 * database, or a listener error all emit the current time instead. Screens fetch on that
 * first emission, so the data still loads — it just stops refreshing live.
 */
function getLastUpdateChannel(firebase, lastUpdatePath: string) {
    return eventChannel((emitter) => {
        let timer: ReturnType<typeof setTimeout> | null = null;
        let emittedOnce = false;
        const emit = (value) => {
            const stamp = typeof value === "number" ? value : Date.now();
            if (!emittedOnce) {
                emittedOnce = true;
                emitter(stamp);
                return;
            }
            if (timer) clearTimeout(timer);
            timer = setTimeout(() => emitter(stamp), SIGNAL_DEBOUNCE_MS);
        };

        const db = firebase?.secondaryDb;
        if (!db) {
            emit(null);
            return () => { };
        }
        const ref = db.ref(firebase.sbp + lastUpdatePath);
        const onValue = (snap) => emit(snap.val());
        ref.on("value", onValue, (error) => {
            console.log("lastUpdate listener failed on", lastUpdatePath, error?.code || error);
            if (!emittedOnce) emit(null);
        });
        return () => {
            if (timer) clearTimeout(timer);
            ref.off("value", onValue);
        };
    }, buffers.sliding(1));
}

export default {
    getLastUpdateChannel
}
